import { useEffect, useMemo, useState } from 'react';
import { http } from '../../api';
import { humaniseRole, humaniseCategory, humaniseCapability } from '../../lib/labels';

type Capability = { capability_key: string; category: string; description: string };
type MatrixEntry = { granted: boolean; source: 'tenant' | 'default' };
type Matrix = Record<string, Record<string, MatrixEntry>>;

const ROLES = ['owner', 'super_admin', 'admin', 'advocate', 'pupil', 'clerk', 'accountant', 'viewer'] as const;
type Role = typeof ROLES[number];

export default function SettingsPermissions() {
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [matrix, setMatrix] = useState<Matrix>({});
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const [caps, mat] = await Promise.all([
          http.get<{ capabilities: Capability[] }>('/permissions/capabilities'),
          http.get<{ matrix: Matrix }>('/permissions/role-matrix'),
        ]);
        setCapabilities(caps.capabilities);
        setMatrix(mat.matrix || {});
      } catch (err) {
        setMessage({ kind: 'err', text: (err as Error).message });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const byCategory = useMemo(() => {
    const map: Record<string, Capability[]> = {};
    for (const c of capabilities) {
      (map[c.category] = map[c.category] || []).push(c);
    }
    return map;
  }, [capabilities]);

  const isGranted = (role: Role, cap: string): boolean => {
    const entry = matrix[role]?.[cap];
    if (!entry) return role === 'owner' || role === 'super_admin';
    return !!entry.granted;
  };

  const toggle = (role: Role, cap: string) => {
    if (role === 'owner' || role === 'super_admin') return; // always granted
    const nextGranted = !isGranted(role, cap);
    setMatrix((m) => ({
      ...m,
      [role]: { ...(m[role] || {}), [cap]: { granted: nextGranted, source: 'tenant' } },
    }));
    setDirty((d) => ({ ...d, [`${role}:${cap}`]: true }));
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const overrides = Object.keys(dirty).map((key) => {
        const [role, capability_key] = key.split(':');
        const entry = matrix[role]?.[capability_key];
        return { role, capability_key, granted: !!entry?.granted };
      });
      await http.put('/permissions/role-matrix', { overrides });
      setMessage({ kind: 'ok', text: `Saved ${overrides.length} override(s).` });
      setDirty({});
    } catch (err) {
      setMessage({ kind: 'err', text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="muted">Loading permissions…</div>;

  return (
    <div className="stack" style={{ maxWidth: 1100 }}>
      <header className="row" style={{ alignItems: 'flex-start' }}>
        <div className="grow">
          <h1>Permissions</h1>
          <p className="muted">Choose which capabilities each role has. Owner + Super-admin always have all capabilities. Changes save as tenant overrides — defaults stay intact.</p>
        </div>
        <button className="primary" onClick={save} disabled={saving || Object.keys(dirty).length === 0}>
          {saving ? 'Saving…' : `Save${Object.keys(dirty).length ? ` (${Object.keys(dirty).length})` : ''}`}
        </button>
      </header>

      {message && (
        <div style={{ color: message.kind === 'ok' ? 'var(--color-success)' : 'var(--color-danger)' }}>
          {message.text}
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table>
          <thead>
            <tr>
              <th style={{ minWidth: 240 }}>Capability</th>
              {ROLES.map((r) => (
                <th key={r} style={{ textAlign: 'center', minWidth: 80 }}>{humaniseRole(r)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(byCategory).map(([cat, caps]) => (
              <>
                <tr key={`cat-${cat}`} style={{ background: 'var(--color-surface-2)' }}>
                  <td colSpan={ROLES.length + 1} style={{
                    fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em',
                    color: 'var(--color-text-muted)', fontWeight: 600, padding: '10px 14px',
                  }}>{humaniseCategory(cat)}</td>
                </tr>
                {caps.map((c) => (
                  <tr key={c.capability_key}>
                    <td>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>{humaniseCapability(c.capability_key)}</div>
                      <div className="muted" style={{ fontSize: 12 }}>{c.description}</div>
                    </td>
                    {ROLES.map((r) => {
                      const locked = r === 'owner' || r === 'super_admin';
                      const granted = isGranted(r, c.capability_key);
                      const changed = dirty[`${r}:${c.capability_key}`];
                      return (
                        <td key={r} style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={granted}
                            onChange={() => toggle(r, c.capability_key)}
                            disabled={locked}
                            style={{
                              width: 'auto', cursor: locked ? 'not-allowed' : 'pointer',
                              outline: changed ? '2px solid var(--color-accent)' : 'none',
                              outlineOffset: 2, borderRadius: 3,
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
